// ============================================
// emissive 仲裁应用 Hook（阶段3｜3.4）
// 订阅 emissiveArbiterStore 状态变化 → 按优先级（告警>选中>悬停>数据变色）
// 把最终 emissive 写入 modelRefsRegistry 对应模型的所有 Mesh 材质。
// SceneEditor 挂载（useBindingApply 同级）。
// ============================================

import { useEffect } from 'react';
import * as THREE from 'three';
import { useEmissiveArbiterStore, computeEmissive } from '../stores/emissiveArbiter';
import { modelRefsRegistry } from '../stores/modelRefsRegistry';
import useSceneStore from '../stores/sceneStore';

export function useEmissiveArbiter() {
  useEffect(() => {
    const applyEmissive = (mesh: THREE.Mesh, emissive: THREE.Color, emissiveIntensity: number) => {
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      mats.forEach((mat) => {
        // 2026-09-14 修复：原实现要求 emissive 与 emissiveIntensity 同时存在，
        // 而 FBX 常见 MeshPhongMaterial/LambertMaterial 只有 emissive（无 intensity）→ 选中高亮对它们全部失效。
        // 兼容处理：无 emissiveIntensity 时用 emissive 颜色 × 强度模拟。
        if ('emissive' in mat) {
          const c = (emissive as any)?.isColor ? (emissive as any) : new THREE.Color(emissive as any);
          if ('emissiveIntensity' in mat) {
            (mat as any).emissive.copy(c);
            (mat as any).emissiveIntensity = emissiveIntensity;
          } else {
            (mat as any).emissive.copy(c).multiplyScalar(Math.min(Math.max(emissiveIntensity, 0), 1));
          }
          mat.needsUpdate = true;
        }
      });
    };
    const applyNode = (nodeId: string) => {
      const handle = modelRefsRegistry.get(nodeId);
      const group = handle?.group;
      if (!group) return;
      const state = useEmissiveArbiterStore.getState().getState(nodeId);
      // 2026-09-08｜阶段3：子构件选中时高亮只作用于该构件节点，其余 mesh 清除高亮
      // 2026-09-14：支持部件多选（selectedSubObjects——快速选中同源部件）；单选取其数组
      const st = useSceneStore.getState();
      const subs = (st.selectedSubObjects && st.selectedSubObjects.length > 0
        ? st.selectedSubObjects
        : (st.selectedSubObject ? [st.selectedSubObject] : [])
      ).filter((x) => x.modelId === nodeId);
      if (subs.length > 0) {
        // 2026-09-14 修复（用户"点材质页签后才高亮"根因）：
        // GLB/FBX 中多个 mesh 常共享同一材质实例——原"单遍遍历"里，目标 mesh 高亮写入共享材质后，
        // 其它使用同一材质的非目标 mesh 又执行"清除"写入，后者覆盖前者 → 高亮被清掉（只有材质独占的部件能亮）。
        // 改为两遍法：①先全模型按"非选中"清除；②再对目标（及其子树）高亮——目标最后写入，共享材质也不会被覆盖。
        const cleared = computeEmissive({ ...state, selected: false });
        group.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            applyEmissive(child, cleared.emissive as any, cleared.emissiveIntensity);
          }
        });
        const res = computeEmissive(state);
        const subEmissive = '#ffa940';
        const subIntensity = Math.max(res.emissiveIntensity, 0.5);
        // 2026-09-14 修复（用户"同类型不同名字的部件全部高亮"根因）：
        // 成排部件（如树木）共享同一材质实例——直接写 emissive 会"传染"给所有同材质 mesh。
        // 高亮前对目标网格的材质做 clone（独立实例，__dtwHighlightCloned 防重复），高亮只作用于选中部件。
        // 多选支持：逐个目标高亮（同源批量选中场景）。
        subs.forEach((sub) => {
          const target = group.getObjectByProperty('uuid', sub.uuid);
          if (!target) return;
          target.traverse((child) => {
            if (!(child instanceof THREE.Mesh)) return;
            const mats = Array.isArray(child.material) ? child.material : [child.material];
            const nextMats = mats.map((m: any) => {
              if (!m) return m;
              if (!m.__dtwHighlightCloned) {
                const c = m.clone();
                // 记录源材质 uuid：同源判定（选中同源部件）在 clone 打散后仍可回溯
                c.__dtwOriginMatUuid = m.__dtwOriginMatUuid || m.uuid;
                c.__dtwHighlightCloned = true;
                return c;
              }
              return m;
            });
            if (nextMats.some((m, i) => m !== mats[i])) {
              child.material = Array.isArray(child.material) ? nextMats : nextMats[0];
            }
            applyEmissive(child, subEmissive as any, subIntensity);
          });
        });
        return;
      }
      // 2026-09-08｜修复：computeEmissive 计算一次复用（避免巨型模型每个 mesh 重复求值）
      const { emissive, emissiveIntensity } = computeEmissive(state);
      group.traverse((child) => {
        if (!(child instanceof THREE.Mesh)) return;
        applyEmissive(child, emissive as any, emissiveIntensity);
      });
    };

    const unsub = useEmissiveArbiterStore.subscribe((s) => {
      // 状态变化：对涉及的 nodeId 全量重应用（简单可靠；节点数少，开销可忽略）
      Object.keys(s.states).forEach(applyNode);
    });
    // 2026-09-08｜阶段3：子构件选中/取消时重新应用高亮（高亮范围在构件 vs 全模型间切换）
    const unsub2 = useSceneStore.subscribe((s, prev) => {
      if (
        s.selectedSubObject !== prev.selectedSubObject ||
        s.selectedSubObjects !== prev.selectedSubObjects
      ) {
        Object.keys(useEmissiveArbiterStore.getState().states).forEach(applyNode);
      }
    });
    return () => {
      unsub();
      unsub2();
    };
  }, []);
}